/**
 * Design-system pieces the RM/Tasks screens need that src/ui doesn't have yet.
 * They follow docs/design-guidelines.md and use only --ds-* tokens, so each can
 * be promoted into src/ui as-is. Until then, import them from here.
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'react-feather';
import { Badge } from '../../../../ui';
import type { BadgeTone } from '../../../../ui';
import type { HealthBand, Task } from '../../../../Tasks/tasksApi';
import './ui-pending.scss';

const cx = (...parts: (string | false | null | undefined)[]): string => parts.filter(Boolean).join(' ');

/* ── Drawer / dialog panel ──────────────────────────────────────────────── */

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name; defaults to the title when it is a string. */
  ariaLabel?: string;
  /** Header title. Leave out to render the body only (the child brings its own header). */
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Extra header content beside the title (e.g. a flag or badge). */
  headerExtra?: React.ReactNode;
  footer?: React.ReactNode;
  /** sm 440px, md 480px, lg 560px. */
  size?: 'sm' | 'md' | 'lg';
  /** 'right' slides in from the edge; 'center' is a centred dialog. */
  placement?: 'right' | 'center';
  children: React.ReactNode;
}

/**
 * Side drawer (or centred dialog) rendered into <body>. Portalled because the
 * theme puts a transform on a page wrapper, and a position:fixed child would
 * resolve against that instead of the viewport. Clicking the backdrop closes it.
 * Sits below reactstrap modals (z-index 1050) so useConfirm dialogs open on top.
 */
export const Drawer: React.FC<DrawerProps> = ({
  open, onClose, ariaLabel, title, subtitle, headerExtra, footer, size = 'sm', placement = 'right', children,
}) => {
  if (!open) return null;
  return createPortal(
    <>
      <div className="rm-drawer-backdrop" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
        className={cx('rm-drawer', `rm-drawer--${size}`, `rm-drawer--${placement}`)}
      >
        {title !== undefined && (
          <div className="rm-drawer__header">
            <div className="rm-drawer__heading">
              <div className="rm-drawer__title-row">
                {headerExtra}
                <h2 className="rm-drawer__title">{title}</h2>
              </div>
              {subtitle && <div className="rm-drawer__subtitle">{subtitle}</div>}
            </div>
            <button type="button" className="rm-icon-btn" aria-label="Close" onClick={onClose}>
              <X size={18} aria-hidden />
            </button>
          </div>
        )}
        <div className={cx('rm-drawer__body', title === undefined && 'rm-drawer__body--bare')}>{children}</div>
        {footer && <div className="rm-drawer__footer">{footer}</div>}
      </div>
    </>,
    document.body,
  );
};

/* ── Headline figure ────────────────────────────────────────────────────── */

export type StatTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger';

/** One headline number with a label and optional hint. Lay several out in a `.rm-stats` row. */
export const StatTile: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: StatTone;
  title?: string;
  className?: string;
}> = ({ label, value, hint, tone = 'neutral', title, className }) => (
  <div className={cx('rm-stat', `rm-stat--${tone}`, className)} title={title}>
    <div className="rm-stat__label">{label}</div>
    <div className="rm-stat__value">{value}</div>
    {hint && <div className="rm-stat__hint">{hint}</div>}
  </div>
);

/* ── Avatar ─────────────────────────────────────────────────────────────── */

/** Photo with an initials fallback (also used when the photo fails to load). sm 28, md 36, lg 56. */
export const Avatar: React.FC<{ name: string; photoUrl?: string | null; size?: 'sm' | 'md' | 'lg'; className?: string }> = ({
  name, photoUrl, size = 'md', className,
}) => {
  const [broken, setBroken] = useState(false);
  const initials =
    (name || '?')
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  if (photoUrl && !broken) {
    return (
      <img
        src={photoUrl}
        alt={name}
        onError={() => setBroken(true)}
        className={cx('rm-avatar', `rm-avatar--${size}`, className)}
      />
    );
  }
  return (
    <span className={cx('rm-avatar', 'rm-avatar--initials', `rm-avatar--${size}`, className)} aria-label={name} role="img">
      {initials}
    </span>
  );
};

/* ── Segmented control ──────────────────────────────────────────────────── */

export interface SegmentOption<T extends string> {
  value: T;
  label: React.ReactNode;
  title?: string;
  disabled?: boolean;
}

/** Pill group for switching views or presets (tabs, date presets, scopes). One value is active. */
export function Segmented<T extends string>({
  options, value, onChange, ariaLabel, size = 'md', className,
}: {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  size?: 'sm' | 'md';
  className?: string;
}): JSX.Element {
  return (
    <div className={cx('rm-segmented', size === 'sm' && 'rm-segmented--sm', className)} role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={cx('rm-segmented__item', o.value === value && 'rm-segmented__item--active')}
          aria-pressed={o.value === value}
          title={o.title}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ── Inline notice ──────────────────────────────────────────────────────── */

/** A tinted inline message (not a toast): warnings, errors and hints that sit in the layout. */
export const Callout: React.FC<{
  tone?: 'info' | 'success' | 'warning' | 'danger' | 'neutral';
  title?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ tone = 'info', title, children, action, className }) => (
  <div className={cx('rm-callout', `rm-callout--${tone}`, className)} role={tone === 'danger' ? 'alert' : undefined}>
    <div className="rm-callout__content">
      {title && <div className="rm-callout__title">{title}</div>}
      {children}
    </div>
    {action}
  </div>
);

/* ── Task-specific status chips ─────────────────────────────────────────── */

/** Task status (from its workflow flags) → badge tone. Same mapping everywhere a task appears. */
export const taskStatusTone = (s: { isInitial: boolean; isTerminal: boolean; isSuccess: boolean }): BadgeTone =>
  s.isTerminal ? (s.isSuccess ? 'success' : 'danger') : s.isInitial ? 'neutral' : 'warning';

export const TaskStatusBadge: React.FC<{
  status: { isInitial: boolean; isTerminal: boolean; isSuccess: boolean; label: string };
  label?: React.ReactNode;
}> = ({ status, label }) => <Badge tone={taskStatusTone(status)}>{label ?? status.label}</Badge>;

export const PRIORITY_TONE: Record<Task['priority'], BadgeTone> = {
  URGENT: 'danger',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'neutral',
};

export const PriorityBadge: React.FC<{ priority: Task['priority']; label?: React.ReactNode }> = ({ priority, label }) => (
  <Badge tone={PRIORITY_TONE[priority] ?? 'neutral'}>{label ?? priority}</Badge>
);

/** Learner health band → tone. healthy 75+, watchlist 60–74, at risk 40–59, critical below 40. */
export const HEALTH_TONE: Record<HealthBand, BadgeTone> = {
  HEALTHY: 'success',
  WATCHLIST: 'warning',
  AT_RISK: 'accent',
  CRITICAL: 'danger',
};

export const HealthBadge: React.FC<{ band: HealthBand; label?: React.ReactNode }> = ({ band, label }) => (
  <Badge tone={HEALTH_TONE[band] ?? 'neutral'}>{label ?? band.replace('_', ' ').toLowerCase()}</Badge>
);

/* ── Legend swatch ──────────────────────────────────────────────────────── */

/**
 * Small coloured square for chart legends. `tone` uses a token; for colours that
 * come from data (one per pod/series) pass `color` — the one place an inline
 * colour is allowed, because the value isn't known until runtime.
 */
export const Swatch: React.FC<{ tone?: BadgeTone; color?: string; shape?: 'square' | 'dot' }> = ({
  tone, color, shape = 'square',
}) => (
  <span
    className={cx('rm-swatch', `rm-swatch--${shape}`, tone && `rm-swatch--${tone}`)}
    style={color ? { background: color } : undefined}
    aria-hidden
  />
);

/** Borderless icon-only button (close, copy, remove-chip). Always give it an aria-label. */
export const IconButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, type, ...rest }, ref) => (
    <button ref={ref} type={type ?? 'button'} className={cx('rm-icon-btn', className)} {...rest} />
  ),
);
IconButton.displayName = 'IconButton';
